import React, { useState } from "react";

import { useApolloClient, useMutation } from "@apollo/client";

import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { FileUpload, FileUploadHandlerParam } from "primereact/fileupload";

/** contexts */
import { useSettings } from "../../context/settings";
import { useUser } from "../../context/users";

/** QUERIES MUTATIONS */
import CREATE_PARAMETER, { UPDATE_PARAMETER } from "../../provider/Parameters/mutation";
import { PARAMETERS } from "../../provider/Parameters/query";
import UPLOAD_FILE, { DELETE_FILE } from "../../provider/Upload/mutation";

/** utils */
import { getGraphsqErros, maskRealBeautify } from "../../utilities/functions";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;
  
  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [fileDelete] = useMutation(DELETE_FILE);

  const toast = React.useRef<any>(null);

  const [records, setRecords] = React.useState<any[]>([]);

  const [loading, setLoading] = React.useState<boolean>(true);
  const [reloading, setReloading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);

  const [franchise, setFranchise] = React.useState<any>(null);
  const [franchises, setFranchises] = React.useState<iFranchise[]>([]);
  const [region, setRegion] = React.useState<any>(null);
  const [regions, setRegions] = React.useState<iRegion[]>([]);

  const emptyRecord: iParameter = {
    name: "",
    key: "",
    value: "0,00",
    franchise_id: franchise_id ? Number(franchise_id) : undefined,
    acting_region_id: acting_region_id ? Number(acting_region_id) : undefined,
  };

  const [taximeterFee, setTaximeterFee] = useState<iParameter>(emptyRecord);
  const [taximeterFeeEdited, setTaximeterFeeEdited] = useState<boolean>(false);

  const [taximeterPrice, setTaximeterPrice] = useState<iParameter>({
    ...emptyRecord,
    name: 'taximeter',
    key: 'price',
    value: '',
  });
  const [taximeterMinPrice, setTaximeterMinPrice] = useState<iParameter>({
    ...emptyRecord,
    name: 'taximeter',
    key: 'minPrice',
    value: '',
  })
  const [taximeterKmPrice, setTaximeterKmPrice] = useState<iParameter>({
    ...emptyRecord,
    name: 'taximeter',
    key: 'kmPrice',
    value: '',
  });
  const [taximeterDistance, setTaximeterDistance] = useState<iParameter>({
    ...emptyRecord,
    name: 'taximeter',
    key: 'distance',
    value: '',
  });
  const [taximeterCurrency, setTaximeterCurrency] = useState<iParameter>({
    ...emptyRecord,
    name: 'taximeter',
    key: 'currency',
    value: '',
  });
  const [taximeterConfigEdited, setTaximeterConfigEdited] = useState<boolean>(false);

  const [useTravelled, setUseTravelled] = useState<iParameter>({
    ...emptyRecord,
    name: 'travelled',
    key: 'use',
    value: '1',
  });

  const [serviceProvider, setServiceProvider] = useState<iParameter>({
    ...emptyRecord,
    name: 'register',
    key: 'serviceProvider',
    value: '0',
  });

  const [indicationGift, setIndicationGift] = useState<iParameter>({
    ...emptyRecord,
    name: 'indication',
    key: 'gift',
    value: '0',
  });
  const [indicationGiftEdited, setIndicationGiftEdited] = useState<boolean>(false);

  const [passengerAppURL, setPassengerAppURL] = useState<iParameter>({
    ...emptyRecord,
    name: 'appURL',
    key: 'passenger',
    value: '',
  });
  const [passengerAppURLEdited, setPassengerAppURLEdited] = useState<boolean>(false);

  const [showPriceNotification, setShowPriceNotification] = useState<iParameter>({
    ...emptyRecord,
    name: 'notification',
    key: 'showPrice',
    value: '1',
  });

  const bannerLimit = 5;

  const [passengerBanners, setPassengerBanners] = useState<iParameter>({
    ...emptyRecord,
    name: 'banners',
    key: 'passenger',
    value: '{data:[]}',
  });
  const [passengerBannersData, setPassengerBannersData] = useState<iBanner[]>([]);
  const [passengerBannersdited, setPassengerBannersEdited] = useState<boolean>(false);
  const menuPassengerBanner = React.useRef<any>(null);

  const [driverBanners, setDriverBanners] = useState<iParameter>({
    ...emptyRecord,
    name: 'banners',
    key: 'driver',
    value: '{data:[]}',
  });
  const [driverBannersData, setDriverBannersData] = useState<iBanner[]>([]);
  const [driverBannersdited, setDriverBannersEdited] = useState<boolean>(false);
  const menuDriverBanner = React.useRef<any>(null);

  const loadRecords = () => {
    const filter: any = {};
  
    if (franchise_id) filter.franchise_id = Number(franchise_id);
  
    if (acting_region_id) filter.acting_region_id = Number(acting_region_id);

    if (franchise?.code) filter.franchise_id = Number(franchise.code);

    if (region?.code) filter.acting_region_id = Number(region.code);

    apollo
      .query({
        query: PARAMETERS,
        variables: {
          ...filter,
        },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.parameters;

        setRecords(data);
        loadParameters(data);
        setFranchises(response.data.franchises);
        setRegions(response.data.regions);
        setLoading(false);
        setReloading(false);
      })
      .catch(error => {
        console.log('PARAMETERS error: ', JSON.stringify(error));
        setRecords([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
        setReloading(false);
      });
  };

  const upload = (
    e: FileUploadHandlerParam,
    index: number,
    key: string,
    driver: boolean = false,
  ) => {
    const file = e.files;

    if (!file || file.length <= 0) return;

    uploadFile({
      variables: {
        file: file[0],
        path: key,
      },
    })
      .then(res => {
        const path = res.data.singleUpload;

        const bannerData: iBanner[] = driver ? driverBannersData : passengerBannersData;

        // Deletando imagem antiga
        const oldPath = bannerData[index].image;
        if (oldPath && oldPath !== '' && oldPath !== path) fileDelete({
          variables: {path: oldPath}
        });

        bannerData[index].image = path;

        driver ? setDriverBannersData(bannerData) : setPassengerBannersData(bannerData);

        driver ? saveDriverBanners(bannerData) : savePassengerBanners(bannerData);
      })
      .catch(error => {
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  };

  const loadParameters = (data: iParameter[]) => {
    data.forEach((param: iParameter) => {
      if (acting_region_id && `${param.acting_region_id}` !== `${acting_region_id}`) return;
      if (franchise_id && `${param.franchise_id}` !== `${franchise_id}`) return;
      if (region && `${param.acting_region_id}` !== `${region.code}`) return;
      if (franchise && `${param.franchise_id}` !== `${franchise.code}`) return;

      if (param.name === 'taximeter') {
        if (param.key === 'fee') setTaximeterFee(param);
        if (param.key === 'price') setTaximeterPrice(param);
        if (param.key === 'minPrice') setTaximeterMinPrice(param);
        if (param.key === 'kmPrice') setTaximeterKmPrice(param);
        if (param.key === 'distance') setTaximeterDistance(param);
        if (param.key === 'currency') setTaximeterCurrency(param);
      }

      if (param.name === 'travelled') {
        if (param.key === 'use') setUseTravelled(param);
      }

      if (param.name === 'register') {
        if (param.key === 'serviceProvider') setServiceProvider(param);
      }

      if (param.name === 'indication') {
        if (param.key === 'gift') setIndicationGift(param);
      }

      if (param.name === 'appURL') {
        if (param.key === 'passenger') setPassengerAppURL(param);
      }

      if (param.name === 'notification') {
        if (param.key === 'showPrice') setShowPriceNotification(param);
      }

      if (param.name === 'banners') {
        const bannerData = JSON.parse(param.value);

        if (param.key === 'passenger') {
          setPassengerBanners(param);

          if (bannerData.data) setPassengerBannersData(bannerData.data);
        }

        if (param.key === 'driver') {
          setDriverBanners(param);

          if (bannerData.data) setDriverBannersData(bannerData.data);
        }
      }
    });
  }

  const reloadParameters = () => {
    setTaximeterFee(emptyRecord);
    setTaximeterPrice({
      ...emptyRecord,
      name: 'taximeter',
      key: 'price',
      value: '',
    });
    setTaximeterMinPrice({
      ...emptyRecord,
      name: 'taximeter',
      key: 'minPrice',
      value: '',
    });
    setTaximeterKmPrice({
      ...emptyRecord,
      name: 'taximeter',
      key: 'kmPrice',
      value: '',
    });
    setTaximeterDistance({
      ...emptyRecord,
      name: 'taximeter',
      key: 'distance',
      value: '',
    });
    setTaximeterCurrency({
      ...emptyRecord,
      name: 'taximeter',
      key: 'currency',
      value: '',
    });

    setUseTravelled({
      ...emptyRecord,
      name: 'travelled',
      key: 'use',
      value: '1',
    });

    setServiceProvider({
      ...emptyRecord,
      name: 'register',
      key: 'serviceProvider',
      value: '0',
    });

    setIndicationGift({
      ...emptyRecord,
      name: 'indication',
      key: 'gift',
      value: '0',
    });

    setPassengerAppURL({
      ...emptyRecord,
      name: 'appURL',
      key: 'passenger',
      value: '',
    });

    setShowPriceNotification({
      ...emptyRecord,
      name: 'notification',
      key: 'showPrice',
      value: '1',
    });

    setPassengerBanners({
      ...emptyRecord,
      name: 'banners',
      key: 'passenger',
      value: '{data:[]}',
    });
    setPassengerBannersData([]);

    setDriverBanners({
      ...emptyRecord,
      name: 'banners',
      key: 'driver',
      value: '{data:[]}',
    });
    setDriverBannersData([]);
  };

  const onNewPassengerBanner = () => {
    if (passengerBannersData.length >= bannerLimit) return;

    const newBanner: iBanner = {image: ''};

    setPassengerBannersData([
      ...passengerBannersData,
      newBanner,
    ]);
  };

  const onPassengerBannerRemove = (index: number) => {
    // Deletando imagem antiga
    const oldPath = passengerBannersData[index].image;
    if (oldPath && oldPath !== '') fileDelete({
      variables: {path: oldPath}
    });

    const bannerData: iBanner[] = passengerBannersData.filter((_item, i) => i !== index);

    setPassengerBannersData(bannerData);

    savePassengerBanners(bannerData);
  };

  const onNewDriverBanner = () => {
    if (driverBannersData.length >= bannerLimit) return;

    const newBanner: iBanner = {image: ''};

    setDriverBannersData([
      ...driverBannersData,
      newBanner,
    ]);
  };

  const onDriverBannerRemove = (index: number) => {
    // Deletando imagem antiga
    const oldPath = driverBannersData[index].image;
    if (oldPath && oldPath !== '') fileDelete({
      variables: {path: oldPath}
    });

    const bannerData: iBanner[] = driverBannersData.filter((_item, i) => i !== index);

    setDriverBannersData(bannerData);

    saveDriverBanners(bannerData);
  };

  const onTaximeterFeeChange = (e: any) => {
    let val = (e.target && e.target.value) || "";

    val = maskRealBeautify(val);

    const newFee: iParameter = {
      ...taximeterFee,
      name: 'taximeter',
      key: 'fee',
      value: maskRealBeautify(val),
    }

    setTaximeterFee(newFee);
    setTaximeterFeeEdited(true);
  };

  const onTaximeterPriceChange = (e: any) => {
    let val = (e.target && e.target.value) || "";

    val = maskRealBeautify(val);

    const newValue: iParameter = {
      ...taximeterPrice,
      name: 'taximeter',
      key: 'price',
      value: maskRealBeautify(val),
    }

    setTaximeterPrice(newValue);
    setTaximeterConfigEdited(true);
  };

  const onTaximeterKmPriceChange = (e: any) => {
    let val = (e.target && e.target.value) || "";

    val = maskRealBeautify(val);

    const newValue: iParameter = {
      ...taximeterKmPrice,
      name: 'taximeter',
      key: 'kmPrice',
      value: maskRealBeautify(val),
    }

    setTaximeterKmPrice(newValue);
    setTaximeterConfigEdited(true);
  };

  const onTaximeterMinPriceChange = (e: any) => {
    let val = (e.target && e.target.value) || "";

    val = maskRealBeautify(val);

    const newValue: iParameter = {
      ...taximeterMinPrice,
      name: 'taximeter',
      key: 'minPrice',
      value: maskRealBeautify(val),
    }

    setTaximeterMinPrice(newValue);
    setTaximeterConfigEdited(true);
  };

  const onTaximeterDistanceChange = (e: any) => {
    let val = (e.target && e.target.value) || 0;

    const newValue: iParameter = {
      ...taximeterDistance,
      name: 'taximeter',
      key: 'distance',
      value: val,
    }

    setTaximeterDistance(newValue);
    setTaximeterConfigEdited(true);
  };

  const onTaximeterCurrencyChange = (e: any) => {
    let val = (e.target && e.target.value) || ""

    const newValue: iParameter = {
      ...taximeterCurrency,
      name: 'taximeter',
      key: 'currency',
      value: val,
    }

    setTaximeterCurrency(newValue);
    setTaximeterConfigEdited(true);
  };

  const onUseTravelledChange = (e: any) => {
    const newValue = e.value ? '1' : '0';

    const newTravelled: iParameter = {
      ...useTravelled,
      name: 'travelled',
      key: 'use',
      value: newValue,
    }

    setUseTravelled(newTravelled);
    let mutation = CREATE_PARAMETER;

    if (useTravelled.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, newTravelled);
  };

  const onServiceProviderChange = (e: any) => {
    const newValue = e.value ? '1' : '0';

    const newServiceProvider: iParameter = {
      ...serviceProvider,
      name: 'register',
      key: 'serviceProvider',
      value: newValue,
    }

    setServiceProvider(newServiceProvider);
    let mutation = CREATE_PARAMETER;

    if (serviceProvider.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, newServiceProvider);
  };

  const onIndicationGiftChange = (e: any) => {
    let val = (e.target && e.target.value) || "";

    val = maskRealBeautify(val);

    const newGift: iParameter = {
      ...indicationGift,
      name: 'indication',
      key: 'gift',
      value: maskRealBeautify(val),
    }

    setIndicationGift(newGift);
    setIndicationGiftEdited(true);
  };

  const onPassengerAppURLChange = (e: any) => {
    let val = (e.target && e.target.value) || "";

    const newURL: iParameter = {
      ...passengerAppURL,
      name: 'appURL',
      key: 'passenger',
      value: val,
    }

    setPassengerAppURL(newURL);
    setPassengerAppURLEdited(true);
  };

  const onShowPriceNotificationChange = (e: any) => {
    const newValue = e.value ? '1' : '0';

    const newShow: iParameter = {
      ...showPriceNotification,
      name: 'notification',
      key: 'showPrice',
      value: newValue,
    }

    setShowPriceNotification(newShow);
    let mutation = CREATE_PARAMETER;

    if (showPriceNotification.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, newShow);
  };

  const saveTaximeterFee = () => {
    let mutation = CREATE_PARAMETER;

    if (taximeterFee.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, taximeterFee);
  }

  const saveTaximeterPrice = () => {
    let mutation = CREATE_PARAMETER;

    if (taximeterPrice.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, taximeterPrice);
  }

  const saveTaximeterKmPrice = () => {
    let mutation = CREATE_PARAMETER;

    if (taximeterKmPrice.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, taximeterKmPrice);
  }

  const saveTaximeterMinPrice = () => {
    let mutation = CREATE_PARAMETER;

    if (taximeterMinPrice.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, taximeterMinPrice);
  }

  const saveTaximeterDistance = () => {
    let mutation = CREATE_PARAMETER;

    if (taximeterDistance.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, taximeterDistance);
  }

  const saveTaximeterCurrency = () => {
    let mutation = CREATE_PARAMETER;

    if (taximeterCurrency.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, taximeterCurrency);
  }

  const saveIndicationGift = () => {
    let mutation = CREATE_PARAMETER;

    if (indicationGift.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, indicationGift);
  }

  const savePassengerAppURL = () => {
    let mutation = CREATE_PARAMETER;

    if (passengerAppURL.id) {
      mutation = UPDATE_PARAMETER;
    }

    saveParameter(mutation, passengerAppURL);
  }

  const savePassengerBanners = (data?: iBanner[]) => {
    let mutation = CREATE_PARAMETER;

    if (passengerBanners.id) {
      mutation = UPDATE_PARAMETER;
    }

    const value: any = {
      data: data || passengerBannersData,
    }

    const newParameter = {
      ...passengerBanners,
      value: JSON.stringify(value),
    }

    setPassengerBanners(newParameter)

    saveParameter(mutation, newParameter);
  }

  const saveDriverBanners = (data?: iBanner[]) => {
    let mutation = CREATE_PARAMETER;

    if (driverBanners.id) {
      mutation = UPDATE_PARAMETER;
    }

    const value: any = {
      data: data || driverBannersData,
    }

    const newParameter = {
      ...driverBanners,
      value: JSON.stringify(value),
    }

    setDriverBanners(newParameter)

    saveParameter(mutation, newParameter);
  }

  const saveParameter = (mutation: any, data: iParameter) => {
    setSaving(true);
    const _record: iParameter = { ...data };

    delete _record.__typename;

    if (_record.id) _record.id = parseInt(`${_record.id}`);

    if (franchise?.code) _record.franchise_id = Number(franchise.code);

    if (region?.code) _record.acting_region_id = Number(region.code);

    if (data.name === 'appURL') delete _record.acting_region_id;

    apollo
      .mutate({
        mutation,
        variables: {
          data: {
            ..._record,
          },
        },
      })
      .then(response => {
        setLoading(false);
        setReloading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Parâmetro salvo com sucesso.", life: 3000 });

        loadRecords();
        setTaximeterFeeEdited(false);
        setIndicationGiftEdited(false);
        setPassengerAppURLEdited(false);
        setSaving(false);
        setTaximeterConfigEdited(false);
        setDriverBannersEdited(false);
        setPassengerBannersEdited(false);
      })
      .catch(error => {
        console.log('saveParameter error: ', JSON.stringify(error));
        setLoading(false);
        setReloading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
        setSaving(false);
      });
  }

  React.useEffect(() => {
    setLoading(true);
    loadRecords();
  }, []);

  React.useEffect(() => {
    setReloading(true);
    reloadParameters();
    loadRecords();
  }, [region]);

  React.useEffect(() => {
    setRegion(null);
    setReloading(true);
    reloadParameters();
    loadRecords();
  }, [franchise]);

  return (
    <div className="grid crud-demo">
      <Toast ref={toast} />
      {loading ? (
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
      ) : (
        <div className="col-12">
          <div className="card">
            <div className="flex flex-column md:flex-row flex-wrap">
              { !franchise_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <label htmlFor="name">Franquia</label>
                  <div className="field col-12 md:col-12">
                    <Dropdown
                      value={franchise}
                      onChange={e => setFranchise(e.value)}
                      options={franchises.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Franquia"
                    />
                  </div>
                </div>
              ) : null }


              { !acting_region_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <label htmlFor="name">Região</label>
                  <div className="field col-12 md:col-12">
                    <Dropdown
                      value={region}
                      onChange={e => setRegion(e.value)}
                      options={regions.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Região"
                    />
                  </div>
                </div>
              ) : null }
            </div>
          </div>
          {reloading ? (
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
          ) : (
            <>
              <div className="card">
                <h5>Taxímetro</h5>

                <label>Taxa</label>
                <div className="field col-12 md:col-4" style={{flexDirection: 'column'}}>
                  <InputText
                    id="fee"
                    type={"text"}
                    value={taximeterFee.value}
                    autoComplete={"new-password"}
                    onChange={e => {
                      onTaximeterFeeChange(e);
                    }}
                    className={classNames("p-3")}
                  />
                  {taximeterFeeEdited ? (
                    saving ? (
                      <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                    ) : (
                      <Button
                      label="Aplicar"
                      className="p-button-raised mr-2 mb-2"
                      onClick={saveTaximeterFee}
                    />
                    )
                  ) : null}
                </div>

                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-2" style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <label>Preço mínimo</label>
                    <InputText
                      id="price"
                      type={"text"}
                      value={taximeterPrice.value || 0}
                      autoComplete={"new-password"}
                      onChange={e => {
                        onTaximeterPriceChange(e);
                      }}
                      className={classNames("p-3")}
                    />
                  </div>

                  <div className="field col-12 md:col-2" style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <label>Preço/Km</label>
                    <InputText
                      id="kmPrice"
                      type={"text"}
                      value={taximeterKmPrice.value || 0}
                      autoComplete={"new-password"}
                      onChange={e => {
                        onTaximeterKmPriceChange(e);
                      }}
                      className={classNames("p-3")}
                    />
                  </div>

                  <div className="field col-12 md:col-2" style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <label>Preço/Minuto</label>
                    <InputText
                      id="minPrice"
                      type={"text"}
                      value={taximeterMinPrice.value || 0}
                      autoComplete={"new-password"}
                      onChange={e => {
                        onTaximeterMinPriceChange(e);
                      }}
                      className={classNames("p-3")}
                    />
                  </div>

                  <div className="field col-12 md:col-2" style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <label>Distância mínima</label>
                    <InputText
                      id="distance"
                      type='number'
                      value={taximeterDistance.value || 0}
                      autoComplete={"new-password"}
                      onChange={e => {
                        onTaximeterDistanceChange(e);
                      }}
                      className={classNames("p-3")}
                    />
                  </div>

                  <div className="field col-12 md:col-2" style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <label>Moeda</label>
                    <InputText
                      id="distance"
                      type='text'
                      value={taximeterCurrency.value || 'R$'}
                      autoComplete={"new-password"}
                      onChange={e => {
                        onTaximeterCurrencyChange(e);
                      }}
                      className={classNames("p-3")}
                    />
                  </div>

                  {taximeterConfigEdited ? (
                    saving ? (
                      <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                    ) : (
                      <Button
                        label="Aplicar"
                        className="p-button-raised mr-2 mb-2"
                        onClick={() => {
                          saveTaximeterPrice();
                          saveTaximeterMinPrice();
                          saveTaximeterKmPrice();
                          saveTaximeterDistance();
                          saveTaximeterCurrency();
                        }}
                      />
                    )
                  ) : null}
                </div>
              </div>

              <div className="card">
                <h5>Desvio de rota</h5>

                <div className="field col-12 md:col-4" style={{flexDirection: 'column'}}>
                  <div className="field col-12 md:col-12">
                    <label htmlFor="is_callcenter">Recalcula desvio de rota?</label>
                    <InputSwitch
                      checked={useTravelled.value === '1'}
                      onChange={e => onUseTravelledChange(e)}
                      style={{
                        marginLeft: 25,
                        verticalAlign: 'middle',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="card">
                <h5>Cadastro de Motoristas</h5>

                <div className="field col-12 md:col-6" style={{flexDirection: 'column'}}>
                  <div className="field col-12 md:col-12">
                    <label htmlFor="is_callcenter">Usa cadastro de prestador de serviço?</label>
                    <InputSwitch
                      checked={serviceProvider.value === '1'}
                      onChange={e => onServiceProviderChange(e)}
                      style={{
                        marginLeft: 25,
                        verticalAlign: 'middle',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="card">
                <h5>Indicação</h5>

                <label>Brinde</label>
                <div className="field col-12 md:col-4" style={{flexDirection: 'column'}}>
                  <InputText
                    id="fee"
                    type={"text"}
                    value={indicationGift.value}
                    autoComplete={"new-password"}
                    onChange={e => {
                      onIndicationGiftChange(e);
                    }}
                    className={classNames("p-3")}
                  />
                  {indicationGiftEdited ? (
                    saving ? (
                      <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                    ) : (
                      <Button
                      label="Aplicar"
                      className="p-button-raised mr-2 mb-2"
                      onClick={saveIndicationGift}
                    />
                    )
                  ) : null}
                </div>

                <label>Link App Passageiro</label>
                <div className="field col-12 md:col-4" style={{flexDirection: 'column'}}>
                  <InputText
                    id="fee"
                    type={"text"}
                    value={passengerAppURL.value}
                    autoComplete={"new-password"}
                    onChange={e => {
                      onPassengerAppURLChange(e);
                    }}
                    className={classNames("p-3")}
                  />
                  {passengerAppURLEdited ? (
                    saving ? (
                      <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                    ) : (
                      <Button
                      label="Aplicar"
                      className="p-button-raised mr-2 mb-2"
                      onClick={savePassengerAppURL}
                    />
                    )
                  ) : null}
                </div>
              </div>

              <div className="card">
                <h5>Notificação</h5>

                <div className="field col-12 md:col-4" style={{flexDirection: 'column'}}>
                  <div className="field col-12 md:col-12">
                    <label htmlFor="is_callcenter">Exibir preço na notificação?</label>
                    <InputSwitch
                      checked={showPriceNotification.value === '1'}
                      onChange={e => onShowPriceNotificationChange(e)}
                      style={{
                        marginLeft: 25,
                        verticalAlign: 'middle',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="card">
                <h5>Publicidade (Banners)</h5>

                <div className="flex align-items-center justify-content-between mb-0 p-0 pb-0 mt-4">
                  <h5 className="m-0">Passageiro</h5>
                  <Button icon="pi pi-bars" className="p-button-text" onClick={event => menuPassengerBanner.current.toggle(event)} />
                  <Menu
                    ref={menuPassengerBanner}
                    popup
                    model={[
                      {
                        label: "Novo banner",
                        icon: "pi pi-fw pi-plus",
                        command(e) {
                          onNewPassengerBanner();
                        },
                      },
                    ]}
                  />
                </div>

                {passengerBannersData.map((banner, index) => (
                  <div className="card">
                    {banner.image === '' ? null : (
                      <div className="flex flex-column md:flex-row">
                        <img
                          src={banner.image}
                          alt={'Banner'}
                          width="100"
                          height={100}
                          style={{ objectFit: "contain" }}
                          className="mt-0 mx-auto mb-5 block shadow-2"
                        />
                      </div>
                    )}

                    <div className="flex align-items-center justify-content-between mb-0 p-0 pb-0 mt-4">
                      <label className="mb-3">Banner {index + 1}</label>
                      <Button icon="pi pi-fw pi-minus" className="p-button-text" onClick={() => onPassengerBannerRemove(index)} />
                    </div>

                    <div className="field col-12 md:col-12">
                      <label className="mb-3">* Imagem</label>

                      <FileUpload
                        chooseLabel="Procurar"
                        auto
                        name="file"
                        customUpload={true}
                        uploadHandler={e => upload(e, index, `${franchise_id || franchise?.code}/${acting_region_id || region?.code}/pbanner/${index}/`)}
                        accept="image/*"
                        maxFileSize={1000000}
                      />
                    </div>

                    <div className="field col-12 md:col-4">
                      <label htmlFor={`passenger-banner-link-${index}`}>Link (opicional)</label>

                      <InputText
                        id={`passenger-banner-link-${index}`}
                        type={"text"}
                        value={banner.link}
                        autoComplete={"new-password"}
                        onChange={e => {
                          const val = (e.target && e.target.value) || "";

                          const bannerData: iBanner[] = [...passengerBannersData];
                          bannerData[index].link = val;

                          setPassengerBannersData(bannerData);

                          setPassengerBannersEdited(true);
                        }}
                        className={classNames("p-3", {})}
                      />
                    </div>
                  </div>
                ))}

                {passengerBannersdited ? (
                  saving ? (
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                  ) : (
                    <Button
                      label="Aplicar"
                      className="p-button-raised mr-2 mb-2"
                      onClick={() => savePassengerBanners()}
                    />
                  )
                ) : null}

                <div className="flex align-items-center justify-content-between mb-0 p-0 pb-0 mt-4">
                  <h5 className="m-0">Motorista</h5>
                  <Button icon="pi pi-bars" className="p-button-text" onClick={event => menuDriverBanner.current.toggle(event)} />
                  <Menu
                    ref={menuDriverBanner}
                    popup
                    model={[
                      {
                        label: "Novo banner",
                        icon: "pi pi-fw pi-plus",
                        command(e) {
                          onNewDriverBanner();
                        },
                      },
                    ]}
                  />
                </div>

                {driverBannersData.map((banner, index) => (
                  <div className="card">
                    {banner.image === '' ? null : (
                      <div className="flex flex-column md:flex-row">
                        <img
                          src={banner.image}
                          alt={'Banner'}
                          width="100"
                          height={100}
                          style={{ objectFit: "contain" }}
                          className="mt-0 mx-auto mb-5 block shadow-2"
                        />
                      </div>
                    )}

                    <div className="flex align-items-center justify-content-between mb-0 p-0 pb-0 mt-4">
                      <label className="mb-3">Banner {index + 1}</label>
                      <Button icon="pi pi-fw pi-minus" className="p-button-text" onClick={() => onDriverBannerRemove(index)} />
                    </div>

                    <div className="field col-12 md:col-12">
                      <label className="mb-3">* Imagem</label>

                      <FileUpload
                        chooseLabel="Procurar"
                        auto
                        name="file"
                        customUpload={true}
                        uploadHandler={e => upload(e, index, `${franchise_id || franchise?.code}/${acting_region_id || region?.code}/pbanner/${index}/`, true)}
                        accept="image/*"
                        maxFileSize={1000000}
                      />
                    </div>

                    <div className="field col-12 md:col-4">
                      <label htmlFor={`driver-banner-link-${index}`}>Link (opicional)</label>

                      <InputText
                        id={`driver-banner-link-${index}`}
                        type={"text"}
                        value={banner.link}
                        autoComplete={"new-password"}
                        onChange={e => {
                          const val = (e.target && e.target.value) || "";

                          const bannerData: iBanner[] = [...driverBannersData];
                          bannerData[index].link = val;

                          setDriverBannersData(bannerData);

                          setDriverBannersEdited(true);
                        }}
                        className={classNames("p-3", {})}
                      />
                    </div>
                  </div>
                ))}

                {driverBannersdited ? (
                  saving ? (
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                  ) : (
                    <Button
                      label="Aplicar"
                      className="p-button-raised mr-2 mb-2"
                      onClick={() => saveDriverBanners()}
                    />
                  )
                ) : null}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Crud;
